import { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import {
    CONTENT_TYPE,
    CONTENT_TYPE_VALUE,
    DEVICES,
    VIEW_TYPE,
} from 'src/constants';
import { IAppPage, IContent, IResponseList } from 'src/interface';
import { DeviceUtils, Logger, UrlUtils } from 'src/utils';
import Emitter from 'src/utils/emitter';
import { useTracking } from 'src/hooks/useTracking';
import ContentService from 'src/services/ContentService';
import { pageSelector } from 'src/selectors/app';
import * as ContentActions from 'src/redux/actions/content';
import { ALIGN_TYPE } from 'src/components';
import { AdBanner, AdNative } from 'src/modules/shared/components/ad';
import NormalItem from './normal';
import PhotoItem from './photo';

interface IProps {
    item: IContent;
    hasLayoutPhoto?: boolean;
    displayTimeIn?: number;
    align?: ALIGN_TYPE;
    lazyloadImage?: boolean;
    tracking?: string;
    className?: string;
    index?: number;
    itemInListUpdateFn?: (index: number, items: IContent[]) => void;
}

export function ContentItem(props: IProps) {
    const {
        item,
        hasLayoutPhoto,
        tracking,
        lazyloadImage,
        index = 0,
        itemInListUpdateFn,
    } = props;
    const { contentTypes = [], viewType } = item;
    const dispatch = useDispatch();
    const page: IAppPage = useSelector(pageSelector);
    const router = useRouter();
    const trackingRef = useTracking(item.contentId, item.tracking || tracking);

    const handleReadContentIdChange = useCallback(
        ({
            currentId,
            mostReadId,
        }: {
            currentId: number;
            mostReadId: number[];
        }) => {
            Logger.debug(
                'readContentIdChange | currentId:',
                currentId,
                '| mostReadId :',
                mostReadId
            );

            if (itemInListUpdateFn && currentId === item.contentId) {
                ContentService.getListRelatedByUser({
                    listId: currentId,
                    listIds: mostReadId.join(','),
                }).then((resp: IResponseList<IContent>) => {
                    if (
                        resp.data &&
                        resp.data.items &&
                        resp.data.items.length
                    ) {
                        const contentIdsExclude =
                            ((window as any)._BM
                                .contentIdsExclude as number[]) || [];

                        const contentIdsDuplicate: { [key: number]: number } =
                            (window as any)._BM.contentIdsDuplicate || {};

                        const _contentIdsDuplicate = Object.keys(
                            contentIdsDuplicate
                        )?.map((key: any) => contentIdsDuplicate[key]);

                        const contents = resp.data.items
                            ?.filter(
                                (c) => !contentIdsExclude.includes(c.contentId)
                            )
                            ?.filter(
                                (c) =>
                                    !_contentIdsDuplicate.includes(c.contentId)
                            );

                        if (contents.length) {
                            let _contents = contents.slice(0, 1);
                            if (
                                resp.data.itemSizeLimit == 2 &&
                                contents.length > 1
                            ) {
                                //add item cuối cùng
                                _contents.push(contents[contents.length - 1]);
                            }
                            itemInListUpdateFn(index, _contents);
                            _contents.forEach((c) => {
                                contentIdsExclude.push(c.contentId);
                            });
                        }
                    }
                })
                .catch((err) => {
                    Logger.error(err?.err, 'getListRelatedByUser fail |', err);
                });
            }
        },
        [item]
    );

    useEffect(() => {
        Emitter.on('readContentIdChange', handleReadContentIdChange);

        return () => {
            Emitter.off('readContentIdChange', handleReadContentIdChange);
        };
    }, [item]);

    const onClick = useCallback(
        (evt: React.MouseEvent) => {
            const isDesktop = DeviceUtils.getDevice() === DEVICES.DESKTOP;
            const isSoccerPage = UrlUtils.isSoccerPage();
            evt.preventDefault();
            evt.stopPropagation();
            const trackData = item.tracking || tracking;
            const _tracking =
                trackData && trackData.indexOf('detail') < 0
                    ? page.type + trackData
                    : trackData;
            (window as any)._BM.sourceTracking = _tracking;
            (window as any)._BM.customTopic = item.customTopic;

            if (isDesktop) {
                window.open(
                    isSoccerPage ? item.url : item.redirectUrl,
                    '_blank'
                );
            } else {
                const { utmSource, utmId } = (window as any)._BM;
                if (utmId === 'web_install_ads' && utmSource) {
                    const link = `https://baomoi.onelink.me/Dfp0?pid=${utmSource}&c=web_install_ads&af_dp=baomoi%3A%2F%2Fcontent%3Fsource%3Dweb_install_ads%26contentId%3D${item.contentId}&deep_link_value=baomoi%3A%2F%2Fcontent%3Fsource%3Dweb_install_ads%26contentId%3D${item.contentId}`;
                    router.push(link);
                    return;
                }
                if (UrlUtils.isContentDetailPage(page.type)) {
                    router.push(item.url);
                } else {
                    dispatch(ContentActions.openContent(item));
                    const _window: any = window;
                    if (_window.AdtimaBaomoi && _window.AdtimaBaomoi.config) {
                        _window.AdtimaBaomoi.config._reqid = 0;
                    }
                }
            }
        },
        [item, page.type]
    );

    if (item.type === CONTENT_TYPE.AD_NATIVE) {
        return (
            <AdNative
                index={index}
                zoneId={(item as any).zoneId}
                lazyloadImage={lazyloadImage}
                tracking={tracking}
                {...props}
            />
        );
    }

    if (item.type === CONTENT_TYPE.AD_BANNER) {
        return (
            <AdBanner
                index={index}
                classNameContainer="xxxxxx"
                elId={(item as any).elId}
            />
        );
    }

    if (
        hasLayoutPhoto &&
        contentTypes.includes(CONTENT_TYPE_VALUE.PHOTO) &&
        viewType === VIEW_TYPE.GRID
    ) {
        return <PhotoItem domRef={trackingRef} onClick={onClick} {...props} />;
    }

    return (
        <>
            <NormalItem domRef={trackingRef} onClick={onClick} {...props} />
        </>
    );
}

export default memo(ContentItem);
